// ===========================================
// Start an email series for one student
// ===========================================
//
// The trigger the whole feature exists for: an admin picks a series, and the
// family starts receiving it.
//
// ⚠️ IT PREVIEWS BEFORE IT STARTS, and that is the point rather than polish. A
// series started on the wrong day, or with a backlog nobody expected, sends real
// email to a real family and CANNOT BE UNDONE — a failed or sent step is never
// retried or recalled (D-C3). So the dialog shows every step with the day it
// would actually go out, how many are already past, and who would receive it,
// before the button that does it.

import { useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useSnackbar } from 'notistack';
import {
  useEmailSequenceStatus,
  useEmailSequences,
  usePreviewSequenceStart,
  useStartSequenceForStudent,
} from '../../../hooks/useEmailSequence';
import { AppLocalizationProvider } from '../../common/AppLocalizationProvider';
import { apiErrorMessage } from '../../../utils/apiError';
import { formatUTCDate, toLocalDateString } from '../../../utils/dateUtils';
import type { EmailSequenceStartPreview } from '../../../types/emailSequence.types';

interface Props {
  open: boolean;
  studentId: string;
  studentName: string;
  onClose: () => void;
}

/**
 * `Wed 9 Sep` — a send day, short because the table repeats it per step.
 *
 * ⚠️ `formatUTCDate`, never `format(new Date(value))`. These are `@db.Date`
 * columns serialised as UTC midnight; a local-timezone formatter renders them
 * one day early for every Australian viewer. That bug is live elsewhere in this
 * very dialog's parent (PRE-2) — do not copy it here.
 */
export function formatSendDay(day: string): string {
  return formatUTCDate(day, { weekday: 'short', day: 'numeric', month: 'short' }, 'en-AU');
}

/** "2 contacts: Jane, Tom" — who this would actually reach. */
export function describeRecipients(preview: EmailSequenceStartPreview): string {
  const names = preview.recipients.map((r) => r.name).filter(Boolean);
  const count = preview.recipientCount;
  const noun = count === 1 ? 'contact' : 'contacts';
  return names.length ? `${count} ${noun}: ${names.join(', ')}` : `${count} ${noun}`;
}

export default function StartEmailSequenceDialog({
  open,
  studentId,
  studentName,
  onClose,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [sequenceId, setSequenceId] = useState('');
  const [anchorDate, setAnchorDate] = useState<Date | null>(null);
  const [skipPastDue, setSkipPastDue] = useState(true);
  const [preview, setPreview] = useState<EmailSequenceStartPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const { data: sequences = [], isLoading, isError } = useEmailSequences();
  const { data: status } = useEmailSequenceStatus();
  const previewMutation = usePreviewSequenceStart();
  const startMutation = useStartSequenceForStudent();

  // Only a series that is switched on can be started — the server refuses an
  // inactive one, so offering it would be a dead end.
  const startable = sequences.filter((s) => s.isActive && !s.archivedAt);
  const selected = startable.find((s) => s.id === sequenceId);
  const needsDate = selected?.anchor === 'DATE';

  // Reset per open, so a previous student's answers never carry over.
  useEffect(() => {
    if (!open) return;
    setSequenceId('');
    setAnchorDate(null);
    setSkipPastDue(true);
    setPreview(null);
    setPreviewError(null);
  }, [open]);

  // Re-preview whenever the answer would change. A stale preview beside a Start
  // button is worse than none: it describes a send that will not happen.
  useEffect(() => {
    setPreview(null);
    setPreviewError(null);
    if (!sequenceId) return;
    if (needsDate && !anchorDate) return;

    let cancelled = false;
    previewMutation
      .mutateAsync({
        sequenceId,
        data: {
          studentId,
          // ⚠️ A BARE `YYYY-MM-DD`, never `toISOString()`. An evening Sydney
          // instant is already tomorrow in UTC, which would shift every step of
          // an exam series by a day.
          ...(needsDate && anchorDate ? { anchorDate: toLocalDateString(anchorDate) } : {}),
          // ⚠️ THE SAME ASSUMPTION THE START WILL USE. A skipped step consumes
          // no send-day, so a preview taken without this shows dates the sweep
          // will not honour — measured on an exam series, the last email was
          // shown landing AFTER the exam when it actually sends the day before.
          skipPastDue,
        },
      })
      .then((result) => {
        if (!cancelled) setPreview(result);
      })
      .catch((err) => {
        if (!cancelled) setPreviewError(apiErrorMessage(err, 'Could not preview this series'));
      });

    return () => {
      cancelled = true;
    };
    // `previewMutation` is recreated each render; depending on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sequenceId, anchorDate, needsDate, studentId, skipPastDue]);

  // ⚠️ When the backlog cannot drain before the anchor, skipping is not really a
  // choice — un-ticking it would send "two days before the exam" after the exam.
  // Forced on, and said out loud rather than silently overridden.
  const skipForced = preview?.overrun === true;
  const effectiveSkip = skipForced ? true : skipPastDue;

  const handleStart = async () => {
    if (!sequenceId) return;
    try {
      await startMutation.mutateAsync({
        sequenceId,
        data: {
          studentId,
          ...(needsDate && anchorDate ? { anchorDate: toLocalDateString(anchorDate) } : {}),
          skipPastDue: effectiveSkip,
        },
      });
      enqueueSnackbar(`Series started for ${studentName}`, { variant: 'success' });
      onClose();
    } catch (err) {
      // The server's own message is the useful one here: "already running",
      // "nobody on this family can be emailed", "needs a date".
      enqueueSnackbar(apiErrorMessage(err, 'Could not start this series'), { variant: 'error' });
    }
  };

  const canStart =
    Boolean(sequenceId) &&
    (!needsDate || Boolean(anchorDate)) &&
    Boolean(preview) &&
    preview!.recipientCount > 0 &&
    !startMutation.isPending;

  return (
    // ⚠️ This wrapper is load-bearing, not decoration. The `Count back from`
    // DatePicker below throws "Can not find the date and time pickers
    // localization context" without a provider ancestor, and NOTHING above this
    // dialog supplies one (ViewStudentDialog does not). Before this was added,
    // selecting a DATE-anchored series crashed the dialog into the error
    // boundary in production — invisible to the suite, which mounted its own
    // provider AND stubbed the DatePicker out.
    <AppLocalizationProvider>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Start an email series for {studentName}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {/*
              ⚠️ Shown HERE as well as on the list page: this is the moment an
              admin commits to a send, and "started" with nothing arriving is the
              silent failure the flag causes.
            */}
            {status?.enabled === false && (
              <Alert severity="warning">
                <AlertTitle>Sending is switched off on this server</AlertTitle>
                The series will start, and its emails will go out once sending is enabled.
              </Alert>
            )}

            {isError ? (
              <Alert severity="error">Could not load the available series.</Alert>
            ) : isLoading ? (
              <Stack alignItems="center" sx={{ py: 3 }}>
                <CircularProgress size={24} />
              </Stack>
            ) : startable.length === 0 ? (
              <Alert severity="info">
                There are no active series to start. Create one under Communications → Email Sequences.
              </Alert>
            ) : (
              <>
                <FormControl fullWidth>
                  {/*
                    ⚠️ `labelId` + `id`, not just `label`. Without the pairing the
                    label is not programmatically associated with the control, so
                    a screen reader announces an unlabelled combobox — and
                    `getByLabelText('Series')` cannot find it either, which is how
                    this was noticed. Most admin Selects in this repo omit it; do
                    not copy that.
                  */}
                  <InputLabel id="start-sequence-series-label">Series</InputLabel>
                  <Select
                    labelId="start-sequence-series-label"
                    id="start-sequence-series"
                    label="Series"
                    value={sequenceId}
                    onChange={(e) => setSequenceId(e.target.value)}
                  >
                    {startable.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                        {s.description ? ` — ${s.description}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {needsDate && (
                  <DatePicker
                    label="Count back from"
                    format="dd/MM/yyyy"
                    value={anchorDate}
                    onChange={(value) => setAnchorDate(value)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        helperText: 'The exam or concert this series counts back from',
                      },
                    }}
                  />
                )}

                {previewMutation.isPending && (
                  <Stack alignItems="center" sx={{ py: 2 }}>
                    <CircularProgress size={24} />
                  </Stack>
                )}

                {previewError && <Alert severity="error">{previewError}</Alert>}

                {preview && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        What this student would receive
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Email</TableCell>
                            <TableCell>Goes out</TableCell>
                            <TableCell align="right" />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {preview.steps.map((step) => (
                            <TableRow key={step.position}>
                              <TableCell>#{step.position + 1}</TableCell>
                              <TableCell>
                                {/*
                                  ⚠️ A SKIPPED STEP HAS NO SEND DAY. Showing one is
                                  how the dialog used to promise a date the sweep
                                  would never honour.
                                */}
                                {step.sendDay ? (
                                  formatSendDay(step.sendDay)
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    Will not send
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {step.skipped ? (
                                  <Chip size="small" variant="outlined" label="Skipped" />
                                ) : (
                                  step.pastDue && (
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      color="warning"
                                      label="Already past"
                                    />
                                  )
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>

                    {preview.pastDueCount > 0 && (
                      <Box>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={effectiveSkip}
                              disabled={skipForced}
                              onChange={(e) => setSkipPastDue(e.target.checked)}
                            />
                          }
                          label={`Skip the ${preview.pastDueCount} email${
                            preview.pastDueCount === 1 ? '' : 's'
                          } already past — they will never be sent for this student`}
                        />
                        {skipForced && anchorDate && (
                          <Alert severity="info" sx={{ mt: 1 }}>
                            {preview.pastDueCount} past email
                            {preview.pastDueCount === 1 ? '' : 's'} cannot all send before{' '}
                            {formatSendDay(toLocalDateString(anchorDate))}, one per day — so they are
                            skipped.
                          </Alert>
                        )}
                      </Box>
                    )}

                    {preview.recipientCount === 0 ? (
                      <Alert severity="error">
                        Nobody on this family can be emailed — there is no contact email on file, or
                        every contact has unsubscribed from email series.
                      </Alert>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {describeRecipients(preview)}
                      </Typography>
                    )}
                  </>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={startMutation.isPending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleStart} disabled={!canStart}>
            {startMutation.isPending ? 'Starting…' : 'Start series'}
          </Button>
        </DialogActions>
      </Dialog>
    </AppLocalizationProvider>
  );
}
